"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState, type ComponentType } from "react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import profile from "@/profiles/profileSingleton";
import { DEFAULT_PUBLIC_LANG } from "@/lib/locale/toContentfulLocale";
import { getLangFromPathname } from "@/lib/url/localizeUrl";
import { handleDynamicDivider } from "@/app/[lang]/[[...slug]]/dynamicDivider";
import { createRichTextOptions } from "@/lib/formatters/formatRichText";
import { resolveContentfulRichTextLinks } from "@/lib/formatters/formatLinks";
import PersonalizationExperience from "@/components/client/Personalization/PersonalizationExperience";
import MXPFormatTabs from "@/components/formatters/MXPFormatTabs";
import DatalayerError from "@/components/client/Datalayer/DatalayerError";
import { Datalayer } from "@/components/client/Datalayer/Datalayer";
import { resolveHeaderElement } from "@/components/formatters/MXPFlexibleHero";
function FallbackNotFound(t0) {
  const $ = _c(11);
  const {
    lang
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DatalayerError errorType="404" errorMessage="Page Not Found" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== lang) {
    t2 = <Datalayer slug="404" locale={lang} siteName="404" />;
    $[1] = lang;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h1 className="text-4xl font-bold text-gray-m9">404</h1>;
    t4 = <h2 className="text-xl font-light text-gray-m7">Page Not Found</h2>;
    t5 = <p className="max-w-md text-gray-m5">The page you are looking for does not exist or has been moved.</p>;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  const t6 = `/${lang}`;
  let t7;
  if ($[6] !== t6) {
    t7 = <Link href={t6} className="rounded bg-blue-trmb px-6 py-2 text-white hover:bg-blue-navy">Go Home</Link>;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t2 || $[9] !== t7) {
    t8 = <div className="flex min-h-[50vh] flex-col items-center justify-center gap-6 p-8 text-center">{t1}{t2}{t3}{t4}{t5}{t7}</div>;
    $[8] = t2;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  return t8;
}
function renderSections(pageContent: any, lang: string, layoutIndex: Record<string, ComponentType<any>>) {
  const layoutIndexNiceName: Record<string, string> = {};
  Object.keys(layoutIndex).forEach(key => {
    const nameArr = key.match(/[A-Z][a-z]+/g);
    const filteredNameArr = nameArr?.filter(word => word !== "Layout");
    let joinedValue = filteredNameArr?.map(w => w.toLowerCase()).join("-");
    if (joinedValue === "faq") joinedValue = "information-list";
    if (joinedValue === "recommendations") joinedValue = "product-recommendations";
    layoutIndexNiceName[key] = joinedValue || "unknown";
  });
  const layoutIndexCount: Record<string, number> = {};
  Object.keys(layoutIndex).forEach(key => {
    layoutIndexCount[key] = 0;
  });
  return pageContent.sections?.map((item: any, index: number) => {
    const resolvedContent = resolveContentfulRichTextLinks(item.content);
    if (item.__typename === "LongTextBody") {
      return <div key={item.sys?.id ? `longtext-${item.sys.id}` : `longtext-${index}`} className="mx-auto mt-12 max-w-7xl px-8 pb-24 richtxt">
          {resolvedContent?.json && documentToReactComponents(resolvedContent.json, createRichTextOptions(lang))}
        </div>;
    }
    if (item.__typename === "Tabs") {
      return <div key={item.sys?.id ? `tabs-${item.sys.id}` : `tabs-${index}`} className="mx-auto max-w-7xl px-8 terra:py-12">
          <MXPFormatTabs value={item} index={index} divider={false} lang={lang} />
        </div>;
    }
    if (item.placeholder) {
      return <div key={item.sys?.id ? `placeholder-${item.sys.id}` : `placeholder-${index}`} className="bg-slate-800 p-20">
          {item.__typename}
        </div>;
    }
    const dividerNeeded = handleDynamicDivider(index, item, pageContent);
    const typeName: string = item.__typename;
    const headerElement = resolveHeaderElement(pageContent.sections, index);
    const ComponentToRender = layoutIndex[typeName];
    if (!ComponentToRender) {
      return <div key={item.sys?.id ? `error-${item.sys.id}` : `error-${index}`} className="bg-red-800 p-20">
          error: {item.__typename}
        </div>;
    }
    layoutIndexCount[typeName] = layoutIndexCount[typeName] + 1;
    if (item?.ntExperiencesCollection?.items?.length > 0) {
      const {
        id
      } = item.sys;
      return <div key={item.sys?.id ? `wrap-${item.sys.id}` : `wrap-${index}`}>
          {profile.NEXT_PUBLIC_MODE === "live-preview" && <div id={`${layoutIndexNiceName[typeName]}-${layoutIndexCount[typeName]}`} className="absolute p-2 text-gray-m9 bg-white z-100">
              {layoutIndexNiceName[typeName]}-{layoutIndexCount[typeName]}
            </div>}
          <PersonalizationExperience key={`${item?.sys?.id}-${id}`} value={item} id={id} BaselineComponent={ComponentToRender} dividerNeeded={dividerNeeded} lang={lang} headerElement={headerElement} />
        </div>;
    }
    return <div key={item.sys?.id ? `wrap-${item.sys.id}` : `wrap-${index}`}>
        {profile.NEXT_PUBLIC_MODE === "live-preview" && <div id={`${layoutIndexNiceName[typeName]}-${layoutIndexCount[typeName]}`} className="absolute p-2 text-gray-m9 bg-white z-100">
            {layoutIndexNiceName[typeName]}-{layoutIndexCount[typeName]}
          </div>}
        <ComponentToRender index={index} value={item} divider={dividerNeeded} lang={lang} headerElement={headerElement} />
      </div>;
  });
}
export default function CmsNotFound() {
  const pathname = usePathname();
  const lang = pathname ? getLangFromPathname(pathname) : DEFAULT_PUBLIC_LANG;
  const [pageContent, setPageContent] = useState<any>(null);
  const [layoutIndex, setLayoutIndex] = useState<Record<string, ComponentType<any>> | null>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    async function loadCmsNotFound() {
      try {
        const response = await fetch(`/api/not-found-content?lang=${encodeURIComponent(lang)}`);
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (cancelled || !data?.pageContent?.sections?.length) {
          return;
        }
        const {
          layoutIndex: index
        } = await import("@/app/[lang]/[[...slug]]/layoutIndex");
        if (cancelled) return;
        setPageContent(data.pageContent);
        setLayoutIndex(index);
      } catch {
        /* fall back to static 404 UI */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    }
    loadCmsNotFound();
    return () => {
      cancelled = true;
    };
  }, [lang]);
  if (!loaded) {
    return <FallbackNotFound lang={lang} />;
  }
  if (!pageContent || !layoutIndex) {
    return <FallbackNotFound lang={lang} />;
  }
  return <>
      <DatalayerError errorType="404" errorMessage="Page Not Found" />
      <Datalayer slug="404" locale={lang} siteName="404" />
      {renderSections(pageContent, lang, layoutIndex)}
    </>;
}